import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { Workout, SplitMetric, BestEffortEntry, HrTimeInZone } from '../../types';
import Navbar from '../Layout/Navbar';
import LogWorkoutDrawer from './LogWorkoutDrawer';
import { useUnits } from '../../contexts/UnitsContext';
import './WorkoutList.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const EFFORT_COLORS: Record<string, string> = {
  Great:    '#3EBD7A',
  Good:     '#C9A84C',
  OK:       '#E0A030',
  Hard:     '#E07878',
  Terrible: '#E06060',
};

const TYPE_ICONS: Record<string, string> = {
  'Easy':              '🏃',
  'Long Run':          '🏔️',
  'Tempo':             '⚡',
  'Intervals':         '🔄',
  'Hill Repeats':      '⛰️',
  'Strength Training': '🏋️',
  'Walk':              '🚶',
  'Ride':              '🚴',
};

// Aerobic cross-training + non-run types — excluded from run mileage in the UI.
const NON_RUN_TYPES = ['Strength Training', 'Rest', 'Walk', 'Ride'];

function fmtDuration(sec: number) {
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtDate(dateStr: string) {
  const [y, mo, d] = dateStr.split('-').map(Number);
  return new Date(y, mo - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

function localDateKey(dateStr: string) {
  return dateStr.split('T')[0];
}

// ─── Workout Detail Modal ──────────────────────────────────────────────────────

interface ModalProps {
  workout: Workout;
  onClose: () => void;
  onDelete: (id: number) => void;
  onAnalysisUpdate: (id: number, analysis: string, analyzedAt: string) => void;
  onEffortUpdate?: (id: number, effort: string) => void;
}

const ZONE_COLORS = ['#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444']; // Z1..Z5

type ZoneInfo = { zone: number; label: string; color: string };

// Fallback bpm→zone resolver — generic textbook ranges, used only when the
// runner's real (LTHR-derived) zones aren't available for the workout.
function hrZone(bpm: number): ZoneInfo {
  if (bpm < 115) return { zone: 1, label: 'Zone 1 · Recovery',   color: ZONE_COLORS[0] };
  if (bpm < 130) return { zone: 2, label: 'Zone 2 · Easy',       color: ZONE_COLORS[1] };
  if (bpm < 145) return { zone: 3, label: 'Zone 3 · Aerobic',    color: ZONE_COLORS[2] };
  if (bpm < 160) return { zone: 4, label: 'Zone 4 · Threshold',  color: ZONE_COLORS[3] };
  return          { zone: 5, label: 'Zone 5 · Max',              color: ZONE_COLORS[4] };
}

// Build a bpm→zone resolver from the runner's real zone boundaries when the
// workout carries them; otherwise fall back to the generic ranges above.
function makeZoneResolver(tiz?: HrTimeInZone | null): (bpm: number) => ZoneInfo {
  if (!tiz || !tiz.zones.length) return hrZone;
  const zs = tiz.zones;
  return (bpm: number) => {
    let z = zs[0];
    for (const cur of zs) { if (bpm >= cur.min) z = cur; }
    return { zone: z.zone, label: `Zone ${z.zone} · ${z.label}`, color: ZONE_COLORS[z.zone - 1] };
  };
}

function fmtPaceSec(secPerKm: number): string {
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtEffortTime(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  return `${m}:${String(s).padStart(2,'0')}`;
}

const EFFORT_OPTIONS = ['Great', 'Good', 'OK', 'Hard', 'Terrible'] as const;

const WorkoutModal: React.FC<ModalProps> = ({ workout, onClose, onDelete, onAnalysisUpdate, onEffortUpdate }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(workout.ai_analysis || '');
  const [analyzedAt, setAnalyzedAt] = useState(workout.ai_analyzed_at || '');
  const [showAnalysis, setShowAnalysis] = useState(!!workout.ai_analysis);
  const [showSplits, setShowSplits] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showEffortPicker, setShowEffortPicker] = useState(false);
  const [currentEffort, setCurrentEffort] = useState<string | null>(workout.perceived_effort || null);
  const { formatPace, formatDist, formatElev, isMetric } = useUnits();

  const effortColor = currentEffort ? EFFORT_COLORS[currentEffort] : EFFORT_COLORS['OK'];
  const icon = workout.workout_type ? TYPE_ICONS[workout.workout_type] : '🏃';
  const tiz = workout.hr_time_in_zone;
  const resolveZone = makeZoneResolver(tiz);
  const zone = workout.avg_heart_rate ? resolveZone(workout.avg_heart_rate) : null;
  const splits = workout.splits_metric || [];
  const efforts = workout.best_efforts || [];

  const handleEffortSelect = async (effort: string) => {
    setCurrentEffort(effort);
    setShowEffortPicker(false);
    try {
      await api.updatePerceivedEffort(workout.id, effort);
      onEffortUpdate?.(workout.id, effort);
    } catch {
      setCurrentEffort(workout.perceived_effort || null);
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const result = await api.analyzeWorkout(workout.id);
      setAnalysis(result.analysis);
      setAnalyzedAt(result.analyzed_at);
      setShowAnalysis(true);
      onAnalysisUpdate(workout.id, result.analysis, result.analyzed_at);
    } finally {
      setAnalyzing(false);
    }
  };

  const renderAnalysis = (text: string) => text
    .replace(/##\s(.+)/g, '<h4>$1</h4>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');

  // Convert split speed (m/s) to pace string in user's unit
  const splitPace = (s: SplitMetric): string => {
    if (!s.avg_speed_ms || s.avg_speed_ms === 0) return '—';
    if (isMetric) {
      return fmtPaceSec(1000 / s.avg_speed_ms) + '/km';
    }
    return fmtPaceSec(1609.34 / s.avg_speed_ms) + '/mi';
  };

  const splitElev = (s: SplitMetric): string => {
    if (s.elevation_diff_m == null) return '';
    const v = isMetric ? s.elevation_diff_m : Math.round(s.elevation_diff_m * 3.281);
    const unit = isMetric ? 'm' : 'ft';
    return `${v > 0 ? '+' : ''}${v}${unit}`;
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="workout-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header" style={{ borderTopColor: effortColor || 'var(--primary-gold)' }}>
          <div className="modal-title-block">
            <span className="modal-icon">{icon}</span>
            <div>
              <div className="modal-type-row">
                <h2 className="modal-workout-type">{workout.workout_type || 'Run'}</h2>
                {workout.strava_activity_id ? (
                  <a
                    className="badge-strava"
                    href={`https://www.strava.com/activities/${workout.strava_activity_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on Strava ↗
                  </a>
                ) : (
                  <span className="badge-manual">Manual Entry</span>
                )}
              </div>
              <div className="modal-date">{fmtDate(localDateKey(workout.workout_date))}</div>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Primary stats */}
        <div className="modal-stats modal-stats-primary">
          <div className="modal-stat">
            <div className="modal-stat-value">{formatDist(workout.distance_miles)}</div>
            <div className="modal-stat-label">Distance</div>
          </div>
          <div className="modal-stat">
            <div className="modal-stat-value">{formatPace(workout.avg_pace_sec_per_mile)}</div>
            <div className="modal-stat-label">Avg Pace</div>
          </div>
          <div className="modal-stat">
            <div className="modal-stat-value">{fmtDuration(workout.duration_seconds)}</div>
            <div className="modal-stat-label">Duration</div>
          </div>
        </div>

        {/* Secondary stats */}
        <div className="modal-stats modal-stats-secondary">
          {workout.elevation_gain_ft != null && workout.elevation_gain_ft > 0 && (
            <div className="modal-stat-sm">
              <div className="modal-stat-sm-value">{formatElev(workout.elevation_gain_ft)}</div>
              <div className="modal-stat-sm-label">Elevation</div>
            </div>
          )}
          {workout.avg_heart_rate && (
            <div className="modal-stat-sm">
              <div className="modal-stat-sm-value" style={{ color: zone?.color }}>
                {workout.avg_heart_rate} bpm
              </div>
              <div className="modal-stat-sm-label">Avg HR</div>
            </div>
          )}
          {workout.max_heart_rate && (
            <div className="modal-stat-sm">
              <div className="modal-stat-sm-value">{workout.max_heart_rate} bpm</div>
              <div className="modal-stat-sm-label">Max HR</div>
            </div>
          )}
          {workout.average_cadence && (
            <div className="modal-stat-sm">
              <div className="modal-stat-sm-value">{workout.average_cadence}</div>
              <div className="modal-stat-sm-label">Cadence spm</div>
            </div>
          )}
          {workout.training_stress_score != null && (
            <div className="modal-stat-sm">
              <div className="modal-stat-sm-value">{Math.round(workout.training_stress_score)}</div>
              <div className="modal-stat-sm-label">TSS</div>
            </div>
          )}
          <div className="modal-stat-sm effort-cell" onClick={() => setShowEffortPicker(!showEffortPicker)}>
            <div className="modal-stat-sm-value" style={{ color: effortColor }}>
              {currentEffort || 'OK'}
            </div>
            <div className="modal-stat-sm-label">Effort {currentEffort ? '' : '(tap)'}</div>
            {showEffortPicker && (
              <div className="effort-picker" onClick={e => e.stopPropagation()}>
                {EFFORT_OPTIONS.map(opt => (
                  <button
                    key={opt}
                    className={`effort-option${currentEffort === opt ? ' active' : ''}`}
                    style={{ color: EFFORT_COLORS[opt] }}
                    onClick={() => handleEffortSelect(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* HR Zone badge */}
        {zone && (
          <div className="modal-hr-zone" style={{ background: `${zone.color}18`, borderColor: `${zone.color}40` }}>
            <span className="modal-hr-zone-dot" style={{ background: zone.color }} />
            <span className="modal-hr-zone-label" style={{ color: zone.color }}>{zone.label}</span>
            <div className="modal-hr-zone-bar">
              {[1,2,3,4,5].map(z => (
                <div key={z} className={`modal-hr-zone-seg${z <= zone.zone ? ' active' : ''}`}
                  style={z <= zone.zone ? { background: zone.color, opacity: 0.2 + (z / zone.zone) * 0.8 } : {}} />
              ))}
            </div>
          </div>
        )}

        {/* Time in HR zones — split-level breakdown */}
        {tiz && tiz.total_seconds > 0 && (
          <div className="modal-section">
            <div className="modal-section-title">Time in Heart Rate Zones</div>
            <div className="tiz-rows">
              {tiz.zones.map(z => (
                <div
                  key={z.zone}
                  className={`tiz-row${z.zone === tiz.predominant ? ' tiz-row-predominant' : ''}`}
                >
                  <span className="tiz-label" style={{ color: ZONE_COLORS[z.zone - 1] }}>
                    Z{z.zone} {z.label}
                  </span>
                  <span className="tiz-range">{z.min}–{z.max}</span>
                  <div className="tiz-track">
                    <div
                      className="tiz-fill"
                      style={{ width: `${z.pct}%`, background: ZONE_COLORS[z.zone - 1] }}
                    />
                  </div>
                  <span className="tiz-time">{z.seconds > 0 ? fmtDuration(z.seconds) : '—'}</span>
                  <span className="tiz-pct">{z.pct}%</span>
                </div>
              ))}
            </div>
            <div className="tiz-hint">
              Estimated from per-kilometer splits. Predominant zone is highlighted.
            </div>
          </div>
        )}

        {/* Per-km Splits */}
        {splits.length > 0 && (
          <div className="modal-section">
            <button className="modal-section-toggle" onClick={() => setShowSplits(p => !p)}>
              <span>Splits · {splits.length} km</span>
              <span>{showSplits ? '▲' : '▼'}</span>
            </button>
            {showSplits && (
              <table className="splits-table">
                <thead>
                  <tr>
                    <th>km</th>
                    <th>Pace</th>
                    {splits.some(s => s.avg_hr) && <th>HR</th>}
                    {splits.some(s => s.elevation_diff_m != null) && <th>Elev</th>}
                  </tr>
                </thead>
                <tbody>
                  {splits.map(s => {
                    const z = s.avg_hr ? resolveZone(s.avg_hr) : null;
                    return (
                      <tr key={s.split}>
                        <td className="split-km">{s.split}</td>
                        <td className="split-pace">{splitPace(s)}</td>
                        {splits.some(sp => sp.avg_hr) && (
                          <td className="split-hr" style={{ color: z?.color }}>
                            {s.avg_hr ? `${Math.round(s.avg_hr)}` : '—'}
                          </td>
                        )}
                        {splits.some(sp => sp.elevation_diff_m != null) && (
                          <td className="split-elev">{splitElev(s)}</td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Best Efforts for this run */}
        {efforts.length > 0 && (
          <div className="modal-section">
            <div className="modal-section-title">Best Efforts This Run</div>
            <div className="modal-efforts-grid">
              {efforts.map((e: BestEffortEntry) => (
                <div key={e.name} className="modal-effort-chip">
                  <span className="effort-chip-name">{e.name}</span>
                  <span className="effort-chip-time">{fmtEffortTime(e.elapsed_time_s)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {workout.notes && (
          <div className="modal-notes">
            <div className="modal-notes-label">Notes</div>
            <p>{workout.notes}</p>
          </div>
        )}

        {/* AI Analysis */}
        <div className="modal-analysis-section">
          {analysis ? (
            <>
              <button className="btn-toggle-analysis" onClick={() => setShowAnalysis(p => !p)}>
                {showAnalysis ? '▲ Hide AI Analysis' : '▼ Show AI Analysis'}
                {analyzedAt && (
                  <span className="analysis-ts">{new Date(analyzedAt).toLocaleDateString()}</span>
                )}
              </button>
              {showAnalysis && (
                <div className="modal-analysis-content"
                  dangerouslySetInnerHTML={{ __html: renderAnalysis(analysis) }} />
              )}
            </>
          ) : (
            <button className="btn-analyze-ai" onClick={handleAnalyze} disabled={analyzing}>
              {analyzing ? <><span className="btn-spinner" /> Analyzing…</> : '✦ Analyze with AI'}
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          {confirmDelete ? (
            <div className="delete-confirm">
              <span>Delete this workout?</span>
              <button className="btn-confirm-delete" onClick={() => { onDelete(workout.id); onClose(); }}>
                Yes, delete
              </button>
              <button className="btn-cancel-delete" onClick={() => setConfirmDelete(false)}>Cancel</button>
            </div>
          ) : (
            <button className="btn-delete-workout" onClick={() => setConfirmDelete(true)}>
              🗑 Delete workout
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Calendar ─────────────────────────────────────────────────────────────

const WorkoutList: React.FC = () => {
  const today = new Date();
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [selectedWorkout, setSelectedWorkout] = useState<Workout | null>(null);
  const [showLogDrawer, setShowLogDrawer] = useState(false);
  const [hoveredDay, setHoveredDay] = useState<string | null>(null);
  const tooltipTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { formatPace, formatDist } = useUnits();

  useEffect(() => { loadWorkouts(); api.trackEvent('workouts_viewed'); }, []);

  const loadWorkouts = async () => {
    try {
      setLoading(true);
      const data = await api.getWorkouts(200, 0);
      setWorkouts(data);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncStrava = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const r = await api.syncStravaActivities(90);
      setSyncMessage(`✓ Synced! Imported: ${r.imported}, Updated: ${r.updated}`);
      await loadWorkouts();
    } catch (e: any) {
      setSyncMessage(`Sync failed: ${e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (id: number) => {
    await api.deleteWorkout(id);
    setWorkouts(prev => prev.filter(w => w.id !== id));
  };

  const handleAnalysisUpdate = (id: number, analysis: string, analyzedAt: string) => {
    setWorkouts(prev => prev.map(w =>
      w.id === id ? { ...w, ai_analysis: analysis, ai_analyzed_at: analyzedAt } : w
    ));
  };

  const handleEffortUpdate = (id: number, effort: string) => {
    setWorkouts(prev => prev.map(w =>
      w.id === id ? { ...w, perceived_effort: effort as Workout['perceived_effort'] } : w
    ));
  };

  // Build date→workouts map
  const byDate: Record<string, Workout[]> = {};
  workouts.forEach(w => {
    const key = localDateKey(w.workout_date);
    if (!byDate[key]) byDate[key] = [];
    byDate[key].push(w);
  });

  // Calendar grid
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = new Date(year, month, 1).getDay(); // 0=Sun
  const todayKey = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // Monthly summary
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  const monthWorkouts = workouts.filter(w => localDateKey(w.workout_date).startsWith(monthKey));
  const monthMiles = monthWorkouts.reduce((s, w) => s + (w.distance_miles || 0), 0);
  const monthRuns = monthWorkouts.length;

  if (loading) return (
    <>
      <Navbar />
      <div className="cal-page"><div className="cal-loading">Loading workouts…</div></div>
    </>
  );

  return (
    <>
      <Navbar />

      {showLogDrawer && (
        <LogWorkoutDrawer
          onClose={() => setShowLogDrawer(false)}
          onSuccess={loadWorkouts}
        />
      )}

      {selectedWorkout && (
        <WorkoutModal
          workout={selectedWorkout}
          onClose={() => setSelectedWorkout(null)}
          onDelete={handleDelete}
          onAnalysisUpdate={handleAnalysisUpdate}
          onEffortUpdate={handleEffortUpdate}
        />
      )}

      <div className="cal-page">
        <div className="cal-container">

          {/* Page header */}
          <div className="cal-header">
            <h1>Workouts</h1>
            <div className="cal-header-actions">
              <button onClick={handleSyncStrava} className="btn-sync-strava" disabled={syncing}>
                {syncing ? (
                  <svg width="220" height="40" viewBox="0 0 220 40"><rect width="220" height="40" rx="4" fill="#FC4C02"/><path d="M22 13L17.5 23L13 13H9.5L16.5 28H21L28 13H22Z" fill="white" opacity="0.6"/><text x="38" y="26" fontFamily="Arial,sans-serif" fontSize="14" fontWeight="bold" fill="white" opacity="0.6">Syncing with STRAVA...</text></svg>
                ) : (
                  <svg width="200" height="40" viewBox="0 0 200 40"><rect width="200" height="40" rx="4" fill="#FC4C02"/><path d="M22 13L17.5 23L13 13H9.5L16.5 28H21L28 13H22Z" fill="white"/><text x="38" y="26" fontFamily="Arial,sans-serif" fontSize="14" fontWeight="bold" fill="white">Sync with STRAVA</text></svg>
                )}
              </button>
              <button className="btn-log-workout" onClick={() => setShowLogDrawer(true)}>
                + Log Workout
              </button>
            </div>
          </div>

          {syncMessage && (
            <div className={`cal-sync-msg ${syncMessage.startsWith('✓') ? 'success' : 'error'}`}>
              {syncMessage}
            </div>
          )}

          {/* Month navigation + summary */}
          <div className="cal-nav">
            <button className="cal-nav-btn" onClick={prevMonth}>◀</button>
            <div className="cal-month-info">
              <h2>{MONTHS[month]} {year}</h2>
              <div className="cal-month-summary">
                {monthRuns} run{monthRuns !== 1 ? 's' : ''} · {formatDist(monthMiles)}
              </div>
            </div>
            <button className="cal-nav-btn" onClick={nextMonth}>▶</button>
          </div>

          {/* Day-of-week headers */}
          <div className="cal-dow-row">
            {DAYS.map(d => <div key={d} className="cal-dow">{d}</div>)}
          </div>

          {/* Calendar grid */}
          <div className="cal-grid">
            {/* Leading empty cells */}
            {Array.from({ length: firstDow }).map((_, i) => (
              <div key={`empty-${i}`} className="cal-cell empty" />
            ))}

            {/* Day cells */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayWorkouts = byDate[dateKey] || [];
              const runWorkouts = dayWorkouts.filter(w => !NON_RUN_TYPES.includes(w.workout_type || ''));
              const strengthWorkouts = dayWorkouts.filter(w => w.workout_type === 'Strength Training');
              const crossWorkouts = dayWorkouts.filter(w => w.workout_type === 'Walk' || w.workout_type === 'Ride');
              const hasRuns = runWorkouts.length > 0;
              const hasStrength = strengthWorkouts.length > 0;
              const hasCross = crossWorkouts.length > 0;
              const totalRunDist = runWorkouts.reduce((s, w) => s + (w.distance_miles || 0), 0);
              const primary = dayWorkouts[0];
              const isToday = dateKey === todayKey;
              const isFuture = new Date(year, month, day) > today;
              // Use first run's effort colour, fallback to first workout's
              const effortSource = runWorkouts[0] ?? primary;
              const effortColor = effortSource?.perceived_effort
                ? EFFORT_COLORS[effortSource.perceived_effort]
                : undefined;

              const cellLabel = hasRuns && hasStrength
                ? 'Run + Strength'
                : hasRuns && runWorkouts.length > 1
                  ? `${runWorkouts.length} runs`
                  : hasRuns
                    ? (runWorkouts[0].workout_type || 'Run')
                    : hasStrength
                      ? 'Strength'
                      : (crossWorkouts[0]?.workout_type || 'Activity');

              return (
                <div
                  key={dateKey}
                  className={[
                    'cal-cell',
                    primary ? 'has-workout' : '',
                    isToday ? 'today' : '',
                    isFuture ? 'future' : '',
                  ].filter(Boolean).join(' ')}
                  style={effortColor ? { '--effort-color': effortColor } as React.CSSProperties : undefined}
                  onClick={() => {
                    if (!primary) return;
                    if (dayWorkouts.length > 1) {
                      // Multiple workouts — show tooltip so user can pick one
                      setHoveredDay(prev => prev === dateKey ? null : dateKey);
                    } else {
                      setSelectedWorkout(primary);
                    }
                  }}
                  onMouseEnter={() => {
                    if (tooltipTimeout.current) clearTimeout(tooltipTimeout.current);
                    if (primary) setHoveredDay(dateKey);
                  }}
                  onMouseLeave={() => {
                    tooltipTimeout.current = setTimeout(() => setHoveredDay(null), 200);
                  }}
                >
                  <span className="cal-day-num">{day}</span>

                  {primary && (
                    <div className="cal-workout-content">
                      <div className="cal-workout-icon">
                        {hasRuns && (TYPE_ICONS[runWorkouts[0].workout_type || ''] || '🏃')}
                        {hasStrength && '🏋️'}
                        {!hasRuns && !hasStrength && hasCross && (TYPE_ICONS[crossWorkouts[0].workout_type || ''] || '🚴')}
                      </div>
                      <div className="cal-workout-dist">
                        {hasRuns
                          ? formatDist(totalRunDist)
                          : hasStrength
                            ? (strengthWorkouts[0].duration_seconds
                                ? `${Math.round(strengthWorkouts[0].duration_seconds / 60)} min`
                                : '—')
                            : crossWorkouts[0]?.distance_miles
                              ? formatDist(crossWorkouts[0].distance_miles)
                              : crossWorkouts[0]?.duration_seconds
                                ? `${Math.round(crossWorkouts[0].duration_seconds / 60)} min`
                                : '—'}
                      </div>
                      <div className="cal-workout-type">{cellLabel}</div>
                    </div>
                  )}

                  {/* Hover tooltip */}
                  {hoveredDay === dateKey && primary && (
                    <div
                      className="cal-tooltip"
                      onMouseEnter={() => {
                        if (tooltipTimeout.current) clearTimeout(tooltipTimeout.current);
                      }}
                      onMouseLeave={() => {
                        tooltipTimeout.current = setTimeout(() => setHoveredDay(null), 200);
                      }}
                    >
                      {dayWorkouts.length > 1 && (
                        <div className="tooltip-title">{dayWorkouts.length} workouts</div>
                      )}
                      {dayWorkouts.map(w => (
                        <div
                          key={w.id}
                          className="tooltip-workout-row"
                          onClick={e => { e.stopPropagation(); setSelectedWorkout(w); setHoveredDay(null); }}
                        >
                          <span className="tooltip-row-icon">{TYPE_ICONS[w.workout_type || ''] || '🏃'}</span>
                          <span className="tooltip-row-type">{w.workout_type || 'Run'}</span>
                          {w.workout_type === 'Strength Training' ? (
                            <span className="tooltip-row-stat">{Math.round((w.duration_seconds || 0) / 60)} min</span>
                          ) : (
                            <>
                              <span className="tooltip-row-stat">{formatDist(w.distance_miles)}</span>
                              <span className="tooltip-row-stat muted">{formatPace(w.avg_pace_sec_per_mile)}</span>
                            </>
                          )}
                        </div>
                      ))}
                      {runWorkouts.length > 1 && (
                        <div className="tooltip-total">Total: {formatDist(totalRunDist)}</div>
                      )}
                      <div className="tooltip-cta">Click row to open ↗</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="cal-legend">
            {Object.entries(EFFORT_COLORS).map(([label, color]) => (
              <div key={label} className="legend-item">
                <span className="legend-dot" style={{ background: color }} />
                {label}
              </div>
            ))}
            <div className="legend-item">
              <span className="legend-dot" style={{ background: 'var(--border-medium)' }} />
              No effort logged
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default WorkoutList;
